import React, { useEffect, useState } from 'react';
import { Table, Modal, Select, Input, message } from 'antd';
import { api, getAdminErrorMessage } from '../lib/api';
import { showBulkActionFeedback } from '../lib/bulkActionFeedback';
import BulkActionModal from '../components/BulkActionModal';
const PAYMENT_MANAGEMENT_VIEW_KEY = 'admin_payment_management_view';

const statusStyle = {
  CREATED: { bg: '#eef2ff', color: '#4f46e5', text: '已创建' },
  PENDING: { bg: '#e0f2fe', color: '#0369a1', text: '处理中' },
  SUCCESS: { bg: '#eafbf1', color: '#157347', text: '成功' },
  FAILED: { bg: '#fff1f2', color: '#be123c', text: '失败' },
  CANCELED: { bg: '#f3f4f6', color: '#4b5563', text: '已取消' }
};

const PaymentManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingStatus, setEditingStatus] = useState('PENDING');
  const [failureReason, setFailureReason] = useState('');
  const [providerPayload, setProviderPayload] = useState('');
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState({ open: false, status: null });
  const [bulkFailureReason, setBulkFailureReason] = useState('');
  const [bulkProviderPayload, setBulkProviderPayload] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [companyBalance, setCompanyBalance] = useState(null);

  useEffect(() => {
    fetchCompanyAccount();
    try {
      const saved = JSON.parse(localStorage.getItem(PAYMENT_MANAGEMENT_VIEW_KEY) || 'null');
      if (!saved) return;
      setKeyword(saved.keyword || '');
      setStatusFilter(saved.statusFilter || 'all');
      setTypeFilter(saved.typeFilter || 'all');
      setPageSize(saved.pageSize || 10);
      setSortBy(saved.sortBy || 'createdAt');
      setSortDirection(saved.sortDirection || 'desc');
    } catch (_) {
      localStorage.removeItem(PAYMENT_MANAGEMENT_VIEW_KEY);
    }
  }, []);

  const fetchCompanyAccount = async () => {
    try {
      const response = await api.get('/api/admin/payments/summary/company-account');
      setCompanyBalance(Number(response.data.balance || 0));
    } catch (_) {
      setCompanyBalance(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrders();
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, statusFilter, typeFilter, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem(PAYMENT_MANAGEMENT_VIEW_KEY, JSON.stringify({
      keyword,
      statusFilter,
      typeFilter,
      pageSize,
      sortBy,
      sortDirection
    }));
  }, [keyword, statusFilter, typeFilter, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [keyword, statusFilter, typeFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/payments', {
        params: {
          keyword: keyword.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          type: typeFilter === 'all' ? undefined : typeFilter,
          page,
          pageSize,
          sortBy,
          sortDirection
        }
      });
      setOrders(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载支付订单失败'));
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (orderId) => {
    try {
      const response = await api.get(`/api/admin/payments/${orderId}`);
      const order = response.data;
      setSelectedOrder(order);
      setEditingStatus(order.status);
      setFailureReason(order.failureReason || '');
      setProviderPayload(order.providerPayload || '');
      setDetailVisible(true);
    } catch (error) {
      message.error(error.response?.data?.message || '加载支付详情失败');
    }
  };

  const saveOrder = async () => {
    if (!selectedOrder) return;
    setSaving(true);
    try {
      const response = await api.post(`/api/admin/payments/${selectedOrder.id}/status`, {
        status: editingStatus,
        failureReason,
        providerPayload
      });
      setSelectedOrder(response.data);
      setEditingStatus(response.data.status);
      setFailureReason(response.data.failureReason || '');
      setProviderPayload(response.data.providerPayload || '');
      message.success('支付订单已更新');
      fetchCompanyAccount();
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '更新支付订单失败');
    } finally {
      setSaving(false);
    }
  };

  const openBulkStatusModal = (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择订单');
      return;
    }
    setBulkAction({ open: true, status });
    setBulkFailureReason('');
    setBulkProviderPayload('');
  };

  const submitBulkStatus = async (ids) => {
    const normalizedIds = ids || selectedRowKeys;
    if (!bulkAction.status || normalizedIds.length === 0) return;
    if ((bulkAction.status === 'FAILED' || bulkAction.status === 'CANCELED') && !bulkFailureReason.trim()) {
      message.warning('请输入失败或取消原因');
      return;
    }
    setBulkSaving(true);
    try {
      const response = await api.post('/api/admin/payments/bulk-status', {
        ids: normalizedIds,
        status: bulkAction.status,
        failureReason: bulkFailureReason.trim() || undefined,
        providerPayload: bulkProviderPayload.trim() || undefined
      });
      showBulkActionFeedback(response.data, {
        entityLabel: '支付订单',
        successVerb: '更新',
        onSelectFailed: response.data.failedIds?.length ? (failedIds) => setSelectedRowKeys(failedIds) : undefined,
        onRetry: response.data.failedIds?.length ? submitBulkStatus : undefined
      });
      if (!response.data.failedIds?.length) {
        setSelectedRowKeys([]);
      }
      setBulkAction({ open: false, status: null });
      setBulkFailureReason('');
      setBulkProviderPayload('');
      fetchOrders();
      fetchCompanyAccount();
    } catch (error) {
      message.error(error.response?.data?.message || '批量更新失败');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkStatus = async () => {
    await submitBulkStatus();
  };

  const resetFilters = () => {
    setKeyword('');
    setStatusFilter('all');
    setTypeFilter('all');
    setSortBy('createdAt');
    setSortDirection('desc');
    setPage(1);
    setPageSize(10);
    localStorage.removeItem(PAYMENT_MANAGEMENT_VIEW_KEY);
  };

  const sortLabel = getPaymentSortLabel(sortBy, sortDirection);

  const columns = [
    {
      title: '订单ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      ellipsis: true,
      sorter: true,
      sortOrder: sortBy === 'id' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'type' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => value === 'DEPOSIT' ? '充值' : '提现'
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'channel' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => ({
        ALIPAY: '支付宝',
        WECHAT: '微信',
        BANK_CARD: '银行卡'
      }[value] || value)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'status' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => {
        const style = statusStyle[value] || statusStyle.CREATED;
        return <span style={pillStyle(style.bg, style.color)}>{style.text}</span>;
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'amount' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    },
    {
      title: '订单号',
      dataIndex: 'outTradeNo',
      key: 'outTradeNo',
      width: 180,
      ellipsis: true
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      sorter: true,
      sortOrder: sortBy === 'createdAt' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <button onClick={() => openDetail(record.id)} style={buttonStyle('primary')}>
          查看处理
        </button>
      )
    }
  ];

  return (
    <div style={{ padding: '8px', background: 'transparent', minHeight: '100%' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2a44', marginBottom: '6px' }}>
          支付管理
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          统一处理充值、提现与支付回执，方便运营跟进订单状态。
        </div>
      </div>

      <div style={summaryCardStyle}>
        <div>
          <div style={{ color: '#6b7280', fontSize: '13px', fontWeight: 600 }}>公司公账余额</div>
          <div style={{ color: '#1f2a44', fontSize: '26px', fontWeight: 800 }}>
            {companyBalance === null ? '—' : `¥${companyBalance.toFixed(2)}`}
          </div>
        </div>
        <div style={{ color: '#7b8794', fontSize: '13px' }}>充值入账，提现出款</div>
      </div>

      <div style={toolbarStyle}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索订单号、ID、目标账户、渠道"
          allowClear
          style={filterInputStyle}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部状态</option>
          <option value="CREATED">已创建</option>
          <option value="PENDING">处理中</option>
          <option value="SUCCESS">成功</option>
          <option value="FAILED">失败</option>
          <option value="CANCELED">已取消</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部类型</option>
          <option value="DEPOSIT">充值</option>
          <option value="WITHDRAW">提现</option>
        </select>
        <button onClick={() => openBulkStatusModal('PENDING')} style={buttonStyle('primary')}>批量处理中</button>
        <button onClick={() => openBulkStatusModal('SUCCESS')} style={buttonStyle('success')}>批量成功</button>
        <button onClick={() => openBulkStatusModal('FAILED')} style={buttonStyle('danger')}>批量失败</button>
        <button onClick={() => openBulkStatusModal('CANCELED')} style={buttonStyle('neutral')}>批量取消</button>
        <button onClick={resetFilters} style={buttonStyle('neutral')}>恢复默认</button>
        <div style={toolbarHintStyle}>
          {selectedRowKeys.length > 0 ? `已选 ${selectedRowKeys.length} 笔 · ` : ''}
          当前排序：{sortLabel} · 共 {total} 条
        </div>
      </div>

      <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '18px', boxShadow: '0 20px 45px rgba(31, 42, 68, 0.08)', overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          onChange={(pagination, _, sorter) => {
            const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setPage(pagination.current || 1);
            setPageSize(pagination.pageSize || 10);
            if (nextSorter?.field) {
              setSortBy(String(nextSorter.field));
              setSortDirection(nextSorter.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSortBy('createdAt');
              setSortDirection('desc');
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }
          }}
          scroll={{ x: 1100 }}
        />
      </div>

      <Modal
        title={selectedOrder ? `支付订单 ${selectedOrder.outTradeNo}` : '支付订单详情'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={720}
      >
        {selectedOrder && (
          <div style={{ display: 'grid', gap: '12px' }}>
            <InfoRow label="订单ID" value={selectedOrder.id} />
            <InfoRow label="类型" value={selectedOrder.type === 'DEPOSIT' ? '充值' : '提现'} />
            <InfoRow label="渠道" value={selectedOrder.channel} />
            <InfoRow label="金额" value={`¥${Number(selectedOrder.amount || 0).toFixed(2)}`} />
            <InfoRow label="订单号" value={selectedOrder.outTradeNo} />
            <InfoRow label="目标账户" value={selectedOrder.targetAccount || '无'} />
            <InfoRow label="关联流水" value={selectedOrder.relatedTransactionId || '无'} />
            <InfoRow label="创建时间" value={selectedOrder.createdAt} />

            <div>
              <div style={labelStyle}>状态</div>
              <Select
                value={editingStatus}
                onChange={setEditingStatus}
                style={{ width: '100%' }}
                options={[
                  { value: 'CREATED', label: '已创建' },
                  { value: 'PENDING', label: '处理中' },
                  { value: 'SUCCESS', label: '成功' },
                  { value: 'FAILED', label: '失败' },
                  { value: 'CANCELED', label: '已取消' }
                ]}
              />
            </div>

            <div>
              <div style={labelStyle}>失败原因</div>
              <Input.TextArea
                rows={3}
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="失败或取消时建议填写"
              />
            </div>

            <div>
              <div style={labelStyle}>渠道回执</div>
              <Input.TextArea
                rows={4}
                value={providerPayload}
                onChange={(e) => setProviderPayload(e.target.value)}
                placeholder="可填写第三方回调摘要或人工处理备注"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDetailVisible(false)} style={buttonStyle('neutral')}>
                关闭
              </button>
              <button onClick={saveOrder} disabled={saving} style={buttonStyle('primary', saving)}>
                {saving ? '保存中...' : '保存处理结果'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <BulkActionModal
        open={bulkAction.open}
        title="批量更新支付状态"
        confirmText="确认批量处理"
        confirmVariant={bulkAction.status === 'FAILED' || bulkAction.status === 'CANCELED' ? 'danger' : bulkAction.status === 'SUCCESS' ? 'success' : 'primary'}
        confirmLoading={bulkSaving}
        onCancel={() => {
          setBulkAction({ open: false, status: null });
          setBulkFailureReason('');
          setBulkProviderPayload('');
        }}
        onConfirm={handleBulkStatus}
        summaryLines={[
          `选中订单：${selectedRowKeys.length} 笔`,
          `目标状态：${getPaymentStatusLabel(bulkAction.status)}`
        ]}
        note="批量处理会按当前选中订单逐条更新，已成功订单如果不允许回退，后端会自动拦截。"
      >
        {(bulkAction.status === 'FAILED' || bulkAction.status === 'CANCELED') && (
          <div>
            <div style={labelStyle}>失败/取消原因</div>
            <Input.TextArea
              rows={3}
              value={bulkFailureReason}
              onChange={(e) => setBulkFailureReason(e.target.value)}
              placeholder="请输入本次批量处理原因"
            />
          </div>
        )}

        <div>
          <div style={labelStyle}>处理备注</div>
          <Input.TextArea
            rows={4}
            value={bulkProviderPayload}
            onChange={(e) => setBulkProviderPayload(e.target.value)}
            placeholder="可选填写渠道回执摘要、人工备注或运营说明"
          />
        </div>
      </BulkActionModal>
    </div>
  );
};

const labelStyle = {
  fontSize: '12px',
  color: '#7b8794',
  fontWeight: 600,
  marginBottom: '6px'
};

const pillStyle = (backgroundColor, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  backgroundColor,
  color,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px'
});

const InfoRow = ({ label, value }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={fieldCardStyle}>{value}</div>
  </div>
);

const fieldCardStyle = {
  padding: '12px 16px',
  backgroundColor: '#f8fbff',
  borderRadius: '14px',
  border: '1px solid #e6eefc',
  color: '#1f2a44',
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all'
};

const summaryCardStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  padding: '18px 20px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: '18px',
  boxShadow: '0 16px 32px rgba(31, 42, 68, 0.06)'
};

const buttonStyle = (variant = 'neutral', disabled = false) => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: disabled ? 'none' : style.boxShadow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    opacity: disabled ? 0.7 : 1
  };
};

const toolbarStyle = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 18px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  borderRadius: '16px',
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const filterInputStyle = {
  flex: '1 1 280px',
  minWidth: '220px'
};

const filterSelectStyle = {
  minWidth: '160px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid #d6e4ff',
  backgroundColor: '#f8fbff',
  color: '#1f2a44'
};

const toolbarHintStyle = {
  marginLeft: 'auto',
  color: '#6b7280',
  fontSize: '13px',
  fontWeight: 600
};

const toAntdSortOrder = (direction) => direction === 'asc' ? 'ascend' : 'descend';

const getPaymentSortLabel = (sortBy, sortDirection) => {
  const labels = {
    createdAt: '创建时间',
    id: '订单ID',
    type: '类型',
    channel: '渠道',
    status: '状态',
    amount: '金额'
  };
  return `${labels[sortBy] || '创建时间'}${sortDirection === 'asc' ? '升序' : '降序'}`;
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    CREATED: '已创建',
    PENDING: '处理中',
    SUCCESS: '成功',
    FAILED: '失败',
    CANCELED: '已取消'
  };
  return labels[status] || status || '-';
};

export default PaymentManagement;
